import { AvatarColorOption, IconNames } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';

import { channelAttributes } from 'utils/channels';

type Money = {
  readonly amount: number;
  readonly currency: string;
};

/**
 * Formats a `Money` value as reported by the API. `amount` is in the
 * currency's minor unit (e.g. cents for EUR/USD, whole units for JPY), per
 * the ISO 4217 exponent - `Intl.NumberFormat` already knows that exponent
 * per-currency, so it's used to convert rather than assuming /100 for every
 * currency, e.g. `10740` + `EUR` -> `€107.40`.
 *
 * Defensive against malformed API data: a missing amount or an invalid
 * currency code (which would make `Intl.NumberFormat` throw) falls back to a
 * dash rather than crashing the whole activity feed, which renders every entry
 * in a single unguarded `map`.
 */
export const formatMoney = (money?: Money): string => {
  if (!money || !Number.isFinite(money.amount)) {
    return '—';
  }
  try {
    const formatter = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: money.currency,
    });
    const { maximumFractionDigits = 0 } = formatter.resolvedOptions();
    return formatter.format(money.amount / 10 ** maximumFractionDigits);
  } catch {
    return '—';
  }
};

type LineItemLike = {
  readonly reference?: string;
  readonly label: string;
  readonly quantity: number;
  readonly amount?: Money;
};

/** The line item's product label, suffixed with its reference when present. */
export const lineItemLabel = (item: LineItemLike): string =>
  item.reference ? `${item.label} (${item.reference})` : item.label;

/** The line item's value column, e.g. `qty 1 · €89.50`. */
export const lineItemValue = (item: LineItemLike): string =>
  [`qty ${item.quantity}`, item.amount ? formatMoney(item.amount) : undefined]
    .filter(Boolean)
    .join(' · ');

/** The total quantity across line items, e.g. `2 items`, or undefined when there are none. */
export const itemCountLabel = (
  items?: readonly { readonly quantity: number }[],
): string | undefined => {
  if (!items?.length) {
    return undefined;
  }
  const count = items.reduce((sum, item) => sum + item.quantity, 0);
  return `${count} item${count === 1 ? '' : 's'}`;
};

/** Known channel icons keyed by their token with underscores stripped, for loose matching. */
const iconByNormalizedChannel = new Map(
  Object.entries(channelAttributes).map(([key, { icon }]) => [
    key.replace(/_/g, ''),
    icon,
  ]),
);

/**
 * Best-effort channel icon from an untyped channel reference (e.g. `contactform`,
 * `pstn_phone`): matches against the known channels ignoring underscores/case,
 * falling back to a generic chat glyph.
 */
export const channelRefIcon = (channelRef?: string): IconNames =>
  (channelRef &&
    iconByNormalizedChannel.get(channelRef.toLowerCase().replace(/_/g, ''))) ||
  'chat';

/** Turns an enum-style token like `WIDGET_CHAT` into a readable `Widget chat`. */
export const humanize = (value: string): string => {
  const lower = value.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

/**
 * Soft tinted circle colors (light background + saturated icon) for the
 * activity avatars, keyed by the semantic status of the event. Values are
 * habla color tokens so they track the active (light/dark) theme.
 */
export const statusColors = {
  positive: {
    backgroundColor: colors.green[100],
    contentColor: colors.green[500],
  },
  negative: {
    backgroundColor: colors.red[100],
    contentColor: colors.red[500],
  },
  info: {
    backgroundColor: colors.blue[100],
    contentColor: colors.blue[500],
  },
  warning: {
    backgroundColor: colors.orange[100],
    contentColor: colors.orange[500],
  },
  neutral: {
    backgroundColor: colors.neutral[200],
    contentColor: colors.neutral[700],
  },
} satisfies Record<string, AvatarColorOption>;
